import { TableExportFormat } from "@selector/shared/openapi";
import { FastaHeaderFormatEnum, TrackEnum } from "@selector/shared/openapi";

import { syntenyViewerDefaultTracks } from "../../../settings.ts";
import type { RangePerContig } from "../../../store/slices/selection-slice.ts";
import type { SubType, TrackType } from "../../../types.ts";

export interface Option<T extends string> {
  value: T;
  label: string;
}

export interface GffTrackType {
  trackType: TrackType | string;
  subTypeId?: number;
  name: string;
}

export interface ContentOption {
  id: string;
  label: string;
  trackType?: TrackEnum;
  subType?: SubType;
  featureIds?: number[];
  rangePerContigs?: RangePerContig[];
}

export const GFF_FEATURE_TYPES = {
  GFF_FEATURE_TYPE_GENE: "gene",
  GFF_FEATURE_TYPE_MRNA: "mRNA",
  GFF_FEATURE_TYPE_EXON: "exon",
  GFF_FEATURE_TYPE_CDS: "CDS",
  GFF_FEATURE_TYPE_TRNA: "tRNA",
  GFF_FEATURE_TYPE_RRNA: "rRNA",
  GFF_FEATURE_TYPE_LNCRNA: "lncRNA",
  GFF_FEATURE_TYPE_NCRNA: "ncRNA",
  GFF_FEATURE_TYPE_SNORNA: "snoRNA",
  GFF_FEATURE_TYPE_SNRNA: "snRNA",
  GFF_FEATURE_TYPE_PSEUDOGENE: "pseudogene",
  GFF_FEATURE_TYPE_AA_FEATURE: "aaFeature",
  GFF_FEATURE_TYPE_NU_FEATURE: "nuFeature",
};

export interface ExportFormValues {
  contentSelect?: string;
  contentOptionMeta?: ContentOption;
  formatSelect?: TableExportFormat;
  fastaHeaderSelect?: FastaHeaderFormatEnum;
  compressed?: boolean;
  reverse?: boolean;
  fileName?: string;
  suffixName?: string;
  featureTypes?: Map<string, GffTrackType>;
}

export const FORMAT_FEATURES_OPTIONS: Option<TableExportFormat>[] = [
  {
    value: TableExportFormat.EXCEL_XLSX,
    label: "Excel (.xslx)",
  },
  {
    value: TableExportFormat.FASTA,
    label: "FASTA Sequence (with headers)",
  },
  {
    value: TableExportFormat.RAW,
    label: "Raw Sequence (without headers)",
  },
];

export const FORMAT_REGION_OPTIONS: Option<TableExportFormat>[] = [
  {
    value: TableExportFormat.FASTA,
    label: "FASTA Sequence (with headers)",
  },
  // TODO Wait for GBK implementation in MT
  // {
  //   value: TableExportFormat.GBK,
  //   label: "GenBank Annotations",
  // },
  {
    value: TableExportFormat.RAW,
    label: "Raw Sequence (without headers)",
  },
];

export const FASTA_HEADERS_OPTIONS: Option<FastaHeaderFormatEnum>[] = [
  {
    value: FastaHeaderFormatEnum.PROFILER_IDENTIFIERS,
    label: "Accession Identifier",
  },
  {
    value: FastaHeaderFormatEnum.SELECTOR_IDENTIFIERS,
    label: "Complete Identifier",
  },
  {
    value: FastaHeaderFormatEnum.VISIBLE_COLUMNS,
    label: "Selected Columns",
  },
];

export function getMatchingGffFeatureType(item: Pick<GffTrackType, "trackType" | "subTypeId">) {
  if (item.subTypeId != undefined) return undefined;
  switch (item.trackType) {
    case TrackEnum.GENE:
      return GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_GENE;
    case TrackEnum.PROTEIN_LOCATION:
      return GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_CDS;
    case TrackEnum.TRANSCRIPT_LOCATION:
      return GFF_FEATURE_TYPES.GFF_FEATURE_TYPE_MRNA;
    default:
      return undefined;
  }
}

/** Get GFF FeatureType key */
export function getGffTrackTypeKey(item: GffTrackType) {
  return `${item.trackType}${item.subTypeId ? ":" + item.subTypeId : ""}`;
}

/** Fasta file extension */
function getFastaFileExtension(trackType?: TrackEnum) {
  if (trackType === TrackEnum.PROTEIN_LOCATION) {
    return ".faa";
  }
  return ".fa";
}

/** Helper: map format -> file extension */
export function getExtensionFromFormat(format: string, trackType?: TrackEnum): string {
  switch (format) {
    case TableExportFormat.EXCEL_XLSX:
      return ".xlsx";
    case TableExportFormat.FASTA:
      return getFastaFileExtension(trackType);
    case TableExportFormat.RAW:
      return ".raw";
    // TODO Wait GBK implementation
    // case TableExportFormat.GBK:
    //   return ".gbk";
    default:
      return "";
  }
}

/** Helpers for dynamic feature content options */
export function getFeatureContentOption(trackType: TrackEnum, subType: SubType | undefined, featureIds: number[]) {
  const subTypeName = subType?.name;
  const trackName = subTypeName ?? syntenyViewerDefaultTracks.find((tracks) => tracks.type === trackType)?.name ?? "";
  const id = subType ? `features:${trackType}:${subType.id}` : `features:${trackType}`;
  const label = `${trackName} Export - ${featureIds.length} selected`;
  return { id, label, trackType, subType, featureIds };
}
